import axios from "axios";

export const basicUrl = `https://py.qubx3d.com/api/v1`;

export const getAuthToken = () => {
  if (typeof window === "undefined") return "";
  try {
    let token = localStorage.getItem("access_token") || "";

    if (typeof token === "string") {
      token = token.trim();
      if (token.startsWith("Bearer ")) {
        token = token.slice(7).trim();
      }
      return token;
    }
    return "";
  } catch (e) {
    return "";
  }
};

export const getRefreshToken = () => {
  if (typeof window === "undefined") return "";
  try {
    let token = localStorage.getItem("refresh_token") || "";

    // Fallback to userOP in case refresh_token wasn't migrated yet
    if (!token) {
      const raw = localStorage.getItem("userOP");
      if (raw) {
        const userOP = JSON.parse(raw);
        token = userOP?.refresh || userOP?.refreshToken || "";
      }
    }

    if (typeof token === "string") {
      token = token.trim();
      if (token.startsWith("Bearer ")) {
        token = token.slice(7).trim();
      }
      return token;
    }
    return "";
  } catch (e) {
    return "";
  }
};

export const isTokenExpired = (token) => {
  if (!token || typeof token !== "string") return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    if (!payload.exp) return false;
    // Buffer of 15 seconds before expiration
    return Date.now() >= payload.exp * 1000 - 15000;
  } catch (e) {
    return false;
  }
};

export const updateUserOPTokens = (newAccessToken, newRefreshToken) => {
  if (typeof window === "undefined") return;
  try {
    // 1. Dedicated separated tokens in localStorage
    if (newAccessToken) {
      localStorage.setItem("access_token", newAccessToken);
      // Clean up any legacy duplicates
      localStorage.removeItem("accessToken");
      localStorage.removeItem("token");
    }
    if (newRefreshToken) {
      localStorage.setItem("refresh_token", newRefreshToken);
      // Clean up any legacy duplicates
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("refresh");
    }

    // 2. Remove access from userOP so userOP only holds profile data
    const raw = localStorage.getItem("userOP");
    if (raw) {
      const userOP = JSON.parse(raw);
      delete userOP.access;
      delete userOP.accessToken;
      delete userOP.token;
      if (newRefreshToken) {
        userOP.refresh = newRefreshToken;
      }
      localStorage.setItem("userOP", JSON.stringify(userOP));
    }
  } catch (e) {
    console.error("Failed to update tokens in localStorage", e);
  }
};

export const clearAuthAndRedirect = () => {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem("userOP");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("refresh");
  } catch (e) {
    // ignore
  }
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// Dedicated unintercepted client for refreshing token to prevent recursive loops
const refreshClient = axios.create({
  baseURL: basicUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;

export const refreshTokenNow = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthAndRedirect();
    throw new Error("No refresh token available");
  }

  refreshPromise = (async () => {
    try {
      const response = await refreshClient.post("/api/token/refresh/", {
        refresh: refreshToken,
      });

      const newAccessToken =
        response.data?.access ||
        response.data?.accessToken ||
        response.data?.token;
      const newRefreshToken =
        response.data?.refresh ||
        response.data?.refreshToken ||
        refreshToken;

      if (!newAccessToken) {
        throw new Error("No access token returned from refresh endpoint");
      }

      updateUserOPTokens(newAccessToken, newRefreshToken);
      return newAccessToken;
    } catch (error) {
      clearAuthAndRedirect();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

export const getValidToken = async () => {
  const token = getAuthToken();
  const refreshToken = getRefreshToken();

  // If token is present and not expired, use it
  if (token && !isTokenExpired(token)) {
    return token;
  }

  // If expired and refresh token exists, refresh proactively
  if (refreshToken) {
    try {
      return await refreshTokenNow();
    } catch (e) {
      return token || "";
    }
  }

  return token || "";
};

export const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const instance = axios.create({
  baseURL: "https://py.qubx3d.com/api/v1",
});
const instanceV2 = axios.create({
  baseURL: "https://py.qubx3d.com/api/v2",
});

// Check if URL is an external third-party service (e.g. Sketchfab, Sirv, CDNs)
export const isExternalEndpoint = (url) => {
  if (!url) return false;
  return (
    (url.startsWith("http://") || url.startsWith("https://")) &&
    !url.includes("qubx3d.com")
  );
};

export const isAuthEndpoint = (url) => {
  if (!url) return false;
  return (
    url.includes("/login") ||
    url.includes("/forgot-password") ||
    url.includes("/api/token/refresh")
  );
};

export const authRequestInterceptor = async (config) => {
  // 1. Skip auth endpoints or external domains (like Sketchfab)
  if (isAuthEndpoint(config.url) || isExternalEndpoint(config.url)) {
    return config;
  }

  // 2. Do NOT overwrite if an Authorization header was already explicitly provided
  config.headers = config.headers || {};
  const existingAuth =
    config.headers.Authorization || config.headers.authorization;
  if (existingAuth) {
    return config;
  }

  const token = await getValidToken();
  if (token) {
    delete config.headers.authorization;
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

export const handleResponseError = async (error, clientInstance) => {
  const originalRequest = error.config;
  const status = error.response?.status;

  // Do NOT attempt token refresh for external endpoints
  if (
    status === 401 &&
    originalRequest &&
    !originalRequest._retry &&
    !isAuthEndpoint(originalRequest.url) &&
    !isExternalEndpoint(originalRequest.url)
  ) {
    originalRequest._retry = true;
    try {
      const newToken = await refreshTokenNow();
      originalRequest.headers = originalRequest.headers || {};
      delete originalRequest.headers.authorization;
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      const retryClient = clientInstance || axios;
      return retryClient(originalRequest);
    } catch (refreshErr) {
      return Promise.reject(refreshErr);
    }
  }

  if (
    status === 401 &&
    (!originalRequest || !originalRequest._retry || isAuthEndpoint(originalRequest?.url)) &&
    !originalRequest?.url?.includes("/login") &&
    !isExternalEndpoint(originalRequest?.url)
  ) {
    clearAuthAndRedirect();
  }

  return Promise.reject(error);
};

instance.interceptors.request.use(authRequestInterceptor);
instanceV2.interceptors.request.use(authRequestInterceptor);
axios.interceptors.request.use(authRequestInterceptor);

instance.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, instance)
);
instanceV2.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, instanceV2)
);
axios.interceptors.response.use(
  (response) => response,
  (error) => handleResponseError(error, axios)
);

export const httpNonToken = instance;
export const httpNonTokenV2 = instanceV2;

export const endPoints = {
  login: "/login", //Login - auth
  requestStatus: "/status", // get all status
  status360: "/status/all/360",
  updateStatus: "/status/update",
  qubxOperator: "/employeeProfile", // get all qubx operator
  updateEmployee: "/employeeProfile/update/",
  sms: "/sms/template", //get all SMS
  smsType: "/sms/request/template/",
  updateSms: "/sms/template/update", //get all SMS
  governorate: "/governorates", //get all governorates
  region: "/regions", //get all governorates
  diconSource: "/diconSource",
  generateLink: "/generate-dicom-link", //generate Url
  user: "/user/",

  requestPrice: "/request/price/",
  price: "/price",
  updatePrice: "/price/update",
  ageGroups: "/agegroup",
  updateAgeGroup: "/agegroup/update",

  //Request
  request: "/request", //get all request tracking
  allRequests: "/request/all",
  updateRequest: "/request/update", //update request Data
  deleteRequest: "/request/delete", //delete request Data

  //patient
  patients: "/patient", // get all patients
  createPatient: "/patient/create",
  updatePatient: "/patient/update",

  //doctor
  doctors: "/doctorProfile", // get all doctor
  createDoctor: "/user/user-doctor/create",
  updateDoctor: "/doctorProfile/update",

  //Printing Provider
  printing: "/printing", //get all Printing Provider
  createPrinting: "/printing/create",
  updatePrinting: "/printing/update",
  requestPrinting: "/request/printing",
  createRequestPrinting: "/request/printing/create",
  updateRequestPrinting: "/request/printing/update",

  //courier
  courier: "/courier", //get all Courier Branches
  createCourier: "/courier/create",
  updateCourier: "/courier/update",
  requestCourier: "/request/courier",
  createRequestCourier: "/request/courier/create",
  updateRequestCourier: "/request/courier/update",

  //Branch
  rcBranch: "/branch", //get all RC Branches
  createRcBranch: "/branch/user/create",
  updateRcBranch: "/branch/update",
  rcGroup: "/rcGroup",
  createRCG: "/rcGroup/create",

  //Notes
  notes: "/note",
  createNote: "/note/create",

  //   notifications
  notifications: "/notifications",
  updateNotification: "/notifications/update",
  updateGroupNotifications: "/notifications/read",

  // send Msg
  sendMsg: "/sms/send",

  // stl file
  stl: "/request/stl",
  updateStl: "/request/stl/reupload",

  // Sirv
  sirv: "/sirv/upload-file",
  deleteSirve: "/sirv/delete-folder-content",

  // get sirv url
  getSirvUrl: "/sirv/get-url-spin",

  // create branch
  createBranch: "branch/user/create",
  getBranch: "branch/",
  updateBranch: "/branch/update",

  // user
  updateUser: "/user/update",
  getUser: "/user",

  // create employee
  employeeCreate: "/user/employee/create",

  changePassword: "/user/changePassword/",

  // service
  serviceType: "/type",
  service: "/service",
  updateService: "/service/update",
  createService: "/service/create",
  deleteService: "/service/delete/",
  //

  // forget-password
  forgetPassword: "/user/forgot-password",

  pathology: "/pathology",
  updatePathology: "/pathology/update",
  createPathology: "/pathology/create",

  anatomy: "/anatomy",
  updateAnatomy: "/anatomy/update",
  createAnatomy: "/anatomy/create",

  customService: "/service/custom",

  //
  statistics: "statistics",
  refreshToken: "/api/token/refresh/",
};
